import axios, { AxiosInstance, AxiosResponse } from 'axios';

import { LinkedTrips, Trip, TripListItem } from '@src/types/api/models';
import { ApiResponse } from '@src/types/api/response/api-response';

import { GetTripsProps, PaginatedResult, TripRepository } from './trip.repository';

export class TripRepositoryAxios implements TripRepository {
  constructor(private readonly axiosInstance: AxiosInstance) {}

  async getAllTrips({ locale }: { locale: string }): Promise<TripListItem[]> {
    try {
      const { data } = await this.errorHandlerWrapper(this.axiosInstance.get(`/reservation/v1?locale=${locale}`));
      return data?.data;
    } catch (e) {
      return [];
    }
  }

  async getTrips({ locale, status, offset, limit, orderBy }: GetTripsProps): Promise<PaginatedResult<TripListItem>> {
    try {
      const searchParams = new URLSearchParams();
      searchParams.set('locale', locale);
      if (status) {
        searchParams.set('status', status.join(','));
      }
      if (offset) {
        searchParams.set('offset', offset.toString());
      }
      if (limit) {
        searchParams.set('limit', limit.toString());
      }
      if (orderBy) {
        searchParams.set('orderBy', orderBy);
      }
      const { data } = await this.errorHandlerWrapper(this.axiosInstance.get(`/reservation/v1?${searchParams}`));
      return data;
    } catch (e) {
      return {
        data: [],
        offset: 0,
        limit: 0,
        count: 0,
        total: 0,
        hasMore: false,
      };
    }
  }

  async getCurrentTrip({ locale }: { locale: string }): Promise<Trip | null> {
    try {
      const { data } = await this.errorHandlerWrapper(
        this.axiosInstance.get<ApiResponse<Trip>>(`/reservation/v1/current?locale=${locale}`),
      );
      return data?.data;
    } catch (e) {
      return null;
    }
  }

  async getTripById({ tripId, locale }: { tripId: string; locale: string }): Promise<Trip | null> {
    try {
      const { data } = await this.errorHandlerWrapper(
        this.axiosInstance.get(`/reservation/v1/${tripId}?locale=${locale}`),
      );
      return data?.data;
    } catch (e) {
      console.log(e);
      return null;
    }
  }

  async getLinkedTrips({ reservationId, locale }: { reservationId: string; locale: string }): Promise<LinkedTrips> {
    const { data } = await this.errorHandlerWrapper(
      this.axiosInstance.get(`/reservation/v1/${reservationId}/linked-reservations?locale=${locale}`),
    );
    return data?.data;
  }

  async linkTripToGuest({
    reservationIdOrCode,
    lastName,
  }: {
    reservationIdOrCode: string;
    lastName: string;
  }): Promise<Trip> {
    try {
      const { data } = await this.errorHandlerWrapper(
        this.axiosInstance.post<ApiResponse<Trip>>('/reservation/v1/link-reservation', {
          reservationIdOrCode,
          lastName,
        }),
      );

      return data?.data;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw e.response?.data.message;
      }
      throw e;
    }
  }

  private async errorHandlerWrapper(request: Promise<AxiosResponse>) {
    try {
      return await request;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const message = `ERROR when calling ${e.request?.path} - error: ${JSON.stringify(e.response?.data)}`;
        // eslint-disable-next-line no-console
        console.log(message);
      }
      throw e;
    }
  }
}
